/**
 * Bracket strategies persistence (SPEC §1A.3, §9.4): the `BracketDoc` (model.ts) is cached in
 * local storage for an instant start and synced **per user** as the versioned document
 * `bracket_presets/main` — one preset list for every account, as Tradovate's ATM strategies are
 * user-level (DESIGN-REFERENCE "Tradovate bracket strategies"; ADR-0017).
 *
 * `bracket_presets` is a field-merge type: on a 409 the keys this window changed (`presets`,
 * `templates_v2`, `order`, `selected`) win and the others come from the server, then the save is
 * retried once (DocSync). What is cached and sent is the stored form (`toStoredBracketDoc`): older
 * builds read only `presets`, which never holds a contract-dependent template (ADR-0049).
 * Storage and the document client are injected so the round trip is unit-tested; the dev preview
 * runs with `sync: false` and its own storage key.
 */
import { DocSync } from "../sync/client.ts";
import type { KeyValueStorage } from "../workspacesSync.ts";
import {
  BRACKET_DOC_ID,
  defaultBracketDoc,
  sanitizeBracketDoc,
  toStoredBracketDoc,
  type BracketDoc,
} from "./model.ts";

type SyncMeta = { version: number; dirty: boolean };

const sameJson = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

function parseJson(s: string | null): unknown {
  if (s === null) return undefined;
  try {
    return JSON.parse(s) as unknown;
  } catch {
    return undefined;
  }
}

export class BracketStore {
  private snap: BracketDoc;
  private readonly listeners = new Set<() => void>();
  private readonly key: string;
  private readonly storage: KeyValueStorage;
  private readonly sync: boolean;
  private readonly docs: DocSync;
  private readonly saveDelayMs: number;
  private meta: SyncMeta;
  /** Server body at `meta.version` (the If-Match base for the field merge). */
  private base: BracketDoc | null = null;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private saving: Promise<void> | null = null;
  private started = false;

  constructor(opts: {
    storageKey: string;
    storage: KeyValueStorage;
    sync: boolean;
    docs?: DocSync;
    saveDelayMs?: number;
  }) {
    this.key = opts.storageKey;
    this.storage = opts.storage;
    this.sync = opts.sync;
    this.docs = opts.docs ?? new DocSync();
    this.saveDelayMs = opts.saveDelayMs ?? 800;
    const stored = sanitizeBracketDoc(parseJson(this.storage.get(this.key)));
    const meta = parseJson(this.storage.get(`${this.key}:sync`)) as Partial<SyncMeta> | undefined;
    this.meta = {
      version: typeof meta?.version === "number" ? meta.version : 0,
      dirty: meta?.dirty === true,
    };
    // First run on this device: the starter examples, not yet saved (the server copy, if any, wins
    // in start() because nothing here is dirty).
    this.snap = stored ?? defaultBracketDoc();
  }

  // ---- subscription (React: useSyncExternalStore) -------------------------------------------

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };

  getSnapshot = (): BracketDoc => this.snap;

  get doc(): BracketDoc {
    return this.snap;
  }

  get syncedVersion(): number {
    return this.meta.version;
  }

  get dirty(): boolean {
    return this.meta.dirty;
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  // ---- mutations ---------------------------------------------------------------------------

  /** Apply a pure change (model.ts helpers); no-op when it returns the same document. */
  update(change: (d: BracketDoc) => BracketDoc): void {
    const next = change(this.snap);
    if (next === this.snap) return;
    this.snap = next;
    this.meta = { ...this.meta, dirty: true };
    this.persistLocal();
    this.emit();
    this.scheduleSave();
  }

  private persistLocal(): void {
    this.storage.set(this.key, JSON.stringify(toStoredBracketDoc(this.snap)));
    this.storage.set(`${this.key}:sync`, JSON.stringify(this.meta));
  }

  // ---- server sync -------------------------------------------------------------------------

  /** Load once per window: a newer server copy wins, else local edits (or the seed) are pushed. */
  async start(): Promise<void> {
    if (!this.sync || this.started) return;
    this.started = true;
    try {
      const server = await this.docs.load<Record<string, unknown>>("bracket_presets", BRACKET_DOC_ID);
      if (server) {
        const body = sanitizeBracketDoc(server.body);
        if (body && (server.version > this.meta.version || !this.meta.dirty)) {
          this.adopt(body, server.version);
          return;
        }
        this.base = body ?? null;
        this.meta = { ...this.meta, version: server.version };
      } else {
        // No server document yet: this window's copy (the seed on a fresh account) becomes it.
        this.meta = { version: 0, dirty: true };
      }
      if (this.meta.dirty) await this.save();
    } catch {
      // API unreachable: keep the local copy; the next change retries.
    }
  }

  /** A `DocChanged` for bracket_presets/main (another device, or our own save echoing back). */
  async refresh(version: number): Promise<void> {
    if (!this.sync || version <= this.meta.version) return;
    try {
      const server = await this.docs.load<Record<string, unknown>>("bracket_presets", BRACKET_DOC_ID);
      if (!server || server.version <= this.meta.version) return;
      const body = sanitizeBracketDoc(server.body);
      if (body) this.adopt(body, server.version);
    } catch {
      /* the next change or reload catches up */
    }
  }

  private adopt(body: BracketDoc, version: number): void {
    this.base = body;
    this.meta = { version, dirty: false };
    if (!sameJson(this.snap, body)) {
      this.snap = body;
      this.emit();
    }
    this.persistLocal();
  }

  private scheduleSave(): void {
    if (!this.sync || !this.started) return;
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      void this.save();
    }, this.saveDelayMs);
    (this.saveTimer as { unref?: () => void }).unref?.();
  }

  /** Save now. Concurrent calls share one request; edits made meanwhile save next. */
  async save(): Promise<void> {
    if (!this.sync) return;
    if (this.saving) {
      await this.saving;
      if (this.meta.dirty) await this.save();
      return;
    }
    this.saving = this.saveOnce();
    try {
      await this.saving;
    } finally {
      this.saving = null;
    }
  }

  private async saveOnce(): Promise<void> {
    const local = this.snap;
    try {
      const r = await this.docs.save(
        "bracket_presets",
        BRACKET_DOC_ID,
        toStoredBracketDoc(this.base ?? local),
        this.meta.version,
        toStoredBracketDoc(local),
      );
      const server = r.kind === "saved" ? r.doc : r.server;
      const body = sanitizeBracketDoc(server.body);
      if (r.kind === "saved" && this.snap !== local) {
        // Edited while the request was in flight: keep those edits, save again on top.
        this.base = body ?? local;
        this.meta = { version: server.version, dirty: true };
        this.persistLocal();
        this.scheduleSave();
        return;
      }
      if (body) this.adopt(body, server.version);
    } catch {
      // Offline: stays dirty; retried on the next change or reload.
    }
  }

  /** Flush a pending debounced save (page hide). */
  flush(): void {
    if (this.saveTimer === null) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    void this.save();
  }
}
